import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { templates } from '@sarnia/shared';
import { api, ApiError } from '../../lib/api';
import { Icon } from '../../components/ui/Icon';
import { templateMatchesQuery } from '../../lib/templateSearch';
import './createSite.css';

/** The blank template has its own "Start from scratch" card, so it is not repeated in the grid. */
const listable = templates.filter((template) => template.id !== 'blank');

type Template = (typeof templates)[number];

type SlugAvailability = {
  status: 'idle' | 'checking' | 'available' | 'unavailable';
  reason?: string;
};

const CLASSIC_FILTER = 'Classic builder';

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/g, '');
}

export function CreateFromTemplatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTemplateId = searchParams.get('template');

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() => {
    if (requestedTemplateId === 'blank') return 'blank';
    if (requestedTemplateId && templates.some((template) => template.id === requestedTemplateId)) {
      return requestedTemplateId;
    }
    return templates[0]?.id ?? 'blank';
  });
  const [siteName, setSiteName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugAvailability, setSlugAvailability] = useState<SlugAvailability>({ status: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [templateQuery, setTemplateQuery] = useState('');

  const filters = [
    'All',
    CLASSIC_FILTER,
    ...Array.from(
      new Set(
        listable
          .filter((template) => template.category)
          .map((template) => template.category as string)
      )
    ),
  ];

  const visibleTemplates = (
    selectedCategory === 'All'
      ? listable
      : selectedCategory === CLASSIC_FILTER
        ? listable.filter((template) => !template.category)
        : listable.filter((template) => template.category === selectedCategory)
  ).filter((template) => templateMatchesQuery(template, templateQuery));

  useEffect(() => {
    const trimmedSlug = slug.trim();

    if (!trimmedSlug) {
      setSlugAvailability({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setSlugAvailability({ status: 'checking' });

    const timer = window.setTimeout(() => {
      api
        .checkSlug(trimmedSlug)
        .then((result) => {
          if (!cancelled) {
            setSlugAvailability(
              result.available
                ? { status: 'available' }
                : { status: 'unavailable', reason: result.reason ?? 'This address is not available.' },
            );
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setSlugAvailability({
              status: 'unavailable',
              reason: err instanceof ApiError ? err.message : 'Could not check this address.',
            });
          }
        });
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [slug]);

  const handleSiteNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.slice(0, 80);
    setSiteName(value);

    if (!slugTouched) {
      setSlug(slugify(value.slice(0, 40)));
    }
  };

  const handleSlugChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSlugTouched(true);
    setSlug(event.target.value.slice(0, 40));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!siteName.trim()) {
      return;
    }

    const trimmedSlug = slug.trim();
    if (trimmedSlug && slugAvailability.status === 'unavailable') {
      setError(slugAvailability.reason ?? 'This address is not available.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { site } = await api.createSite({
        templateId: selectedTemplateId,
        title: siteName.trim(),
        slug: trimmedSlug || undefined,
      });
      // The scratch template always starts the tour; any other template starts it once, for someone who has not seen it yet.
      let tourSeen = false;
      try {
        tourSeen = window.localStorage.getItem('sarnia_tour_done') === '1';
      } catch {
        tourSeen = false;
      }
      navigate(selectedTemplateId === 'blank' || !tourSeen ? `/editor/${site.id}?tour=1` : `/editor/${site.id}`);
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not create the site. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="cs-page">
      <div className="cs-header">
        <div>
          <h1>Create a new site</h1>
          <p className="cs-header__subtitle">Pick a starting point, then choose a name and address.</p>
        </div>
        <Link className="sarnia-btn sarnia-btn--secondary" to="/dashboard/sites">
          Back to my sites
        </Link>
      </div>

      <div className="cs-layout">
        <div className="cs-main">
          <div className="cs-blank-card">
            <button
              type="button"
              role="radio"
              aria-checked={selectedTemplateId === 'blank'}
              className={`cs-blank-select${selectedTemplateId === 'blank' ? ' cs-blank-select--selected' : ''}`}
              onClick={() => setSelectedTemplateId('blank')}
            >
              <span className="cs-blank-icon" aria-hidden="true">
                <Icon name="file-plus" size={28} />
              </span>
              <span className="cs-blank-body">
                <span className="cs-blank-title">Start from scratch</span>
                <span className="cs-blank-description">
                  A blank canvas with a guided tutorial that teaches you the editor as you build.
                </span>
              </span>
              {selectedTemplateId === 'blank' && (
                <span className="cs-blank-check" aria-hidden="true">
                  <Icon name="check" size={18} />
                </span>
              )}
            </button>
          </div>

          <input
            type="search"
            className="cs-search"
            placeholder="Search templates: salon, plumber, wedding, cafe..."
            aria-label="Search templates"
            value={templateQuery}
            onChange={(event) => setTemplateQuery(event.target.value)}
          />

          <div className="cs-category-chips" aria-label="Filter templates">
            {filters.map((filter) => (
              <button
                key={filter}
                type="button"
                className={`cs-category-chip${selectedCategory === filter ? ' cs-category-chip--selected' : ''}`}
                aria-pressed={selectedCategory === filter}
                onClick={() => setSelectedCategory(filter)}
              >
                {filter}
              </button>
            ))}
          </div>

          <div role="radiogroup" aria-label="Choose a template" className="cs-template-grid">
            {visibleTemplates.length === 0 ? (
              <p className="dw-muted" role="status">
                No templates match &ldquo;{templateQuery}&rdquo;. Try a broader word like food, health or shop.
              </p>
            ) : null}
            {visibleTemplates.map((template: Template) => {
              const selected = template.id === selectedTemplateId;
              const swatchColors = [
                template.site.theme.palette.background,
                template.site.theme.palette.accent,
                template.site.theme.palette.surface,
              ];

              return (
                <div
                  key={template.id}
                  className={`cs-template-card${selected ? ' cs-template-card--selected' : ''}`}
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className="cs-template-select"
                    onClick={() => setSelectedTemplateId(template.id)}
                  >
                    {template.previewImage ? (
                      <img
                        className="cs-template-preview-image"
                        src={template.previewImage}
                        alt=""
                        loading="lazy"
                      />
                    ) : (
                      <span className="cs-swatches" aria-hidden="true">
                        {swatchColors.map((color) => (
                          <span
                            key={color}
                            className="cs-swatch"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </span>
                    )}
                    <span className="cs-template-body">
                      <span className="cs-template-name">{template.name}</span>
                      <span className="cs-template-description">{template.description}</span>
                    </span>
                  </button>
                  <a
                    className="cs-preview-link"
                    href={`/templates/${template.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Preview
                  </a>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="cs-summary">
          <form className="cs-summary-card" onSubmit={handleSubmit}>
            <h2>Site details</h2>
            <div className="cs-field">
              <label className="cs-label" htmlFor="site-name">
                Site name
              </label>
              <input
                id="site-name"
                className="cs-input"
                type="text"
                required
                maxLength={80}
                value={siteName}
                onChange={handleSiteNameChange}
                placeholder="My new site"
              />
            </div>

            <div className="cs-field">
              <label className="cs-label" htmlFor="site-slug">
                Address
              </label>
              <input
                id="site-slug"
                className="cs-input"
                type="text"
                maxLength={40}
                value={slug}
                onChange={handleSlugChange}
                aria-describedby="site-slug-status"
                placeholder="my-new-site"
              />
              <p
                id="site-slug-status"
                className={`cs-slug-status ${
                  slugAvailability.status === 'checking'
                    ? 'cs-slug-status--checking'
                    : slugAvailability.status === 'available'
                      ? 'cs-slug-status--available'
                      : slugAvailability.status === 'unavailable'
                        ? 'cs-slug-status--unavailable'
                        : ''
                }`}
              >
                {slugAvailability.status === 'checking'
                  ? 'Checking…'
                  : slugAvailability.status === 'available'
                    ? 'Available'
                    : slugAvailability.status === 'unavailable'
                      ? slugAvailability.reason ?? 'This address is not available.'
                      : 'Lowercase letters, numbers and hyphens.'}
              </p>
            </div>

            {error ? (
              <div role="alert" className="cs-error">
                {error}
              </div>
            ) : null}

            <div className="cs-actions">
              <button
                type="submit"
                className="sarnia-btn sarnia-btn--primary"
                disabled={
                  submitting || (slug.trim().length > 0 && slugAvailability.status === 'unavailable')
                }
              >
                {submitting ? 'Creating…' : 'Create site'}
              </button>
            </div>
          </form>
        </aside>
      </div>
    </div>
  );
}
